import { BlobClient, ContainerClient } from '@azure/storage-blob';

import { Variables } from '@/services/backstage/config';
import { useVariable } from '@softwareimaging/backstage';
import { z } from 'zod';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';

export const useCreateImageUploadSession = () => {
  return useAPIMutation(client => async () => {
    const response = await client.images.createImageUploadSession();
    if (response.status !== 200) {
      throw new Error('Failed to create image upload session');
    }
    return response.body.uri;
  });
};

export const useDownloadStorageItem = () => {
  return useAPIMutation(client => async ({ path }: { path: string }) => {
    const response = await client.images.downloadStorageItem({
      query: { path }
    });
    if (response.status !== 200) {
      throw new Error('Failed to download storage item');
    }
    return response.body.uri;
  });
};

interface UploadImageArgs {
  name: string;
  extension: string;
  file: File | Blob;
  metadata?: Record<string, string>;
}

export const useUploadImage = () => {
  const imageBaseURL = useVariable<Variables>('imageBaseUrl');
  return useAPIMutation((client, query) => async ({ file, name, extension, metadata }: UploadImageArgs) => {
    const response = await client.images.createImageUploadSession();
    if (response.status !== 200) {
      throw new Error('Failed to create image upload session');
    }
    const uri = response.body.uri;
    const containerClient = new ContainerClient(uri);
    const blobClient = containerClient.getBlockBlobClient(`${name}.${extension}`);
    await blobClient.uploadData(file, { metadata });
    await query.invalidateQueries(['gallery']);
    return `${imageBaseURL}/${name}.${extension}`;
  });
};

interface DeleteImageArgs {
  imageName: string;
}

export const useDeleteImage = () => {
  return useAPIMutation((client, query) => async ({ imageName }: DeleteImageArgs) => {
    const response = await client.images.downloadStorageItem({
      query: { path: `assets/${imageName}` }
    });

    if (response.status !== 200) {
      throw new Error('Failed to create blob session');
    }
    const uri = response.body.uri;
    const blob = new BlobClient(uri);

    await blob.deleteIfExists();
    await query.invalidateQueries(['gallery']);
  });
};

interface UploadLogoArgs {
  id: string;
  file: File;
}

export const useUploadLogo = () => {
  const logosBaseURL = useVariable<Variables>('logosBaseUrl');
  return useAPIMutation((client, query) => async ({ file, id }: UploadLogoArgs) => {
    const response = await client.images.createUploadSession({
      query: { path: 'logos' }
    });
    if (response.status !== 200) {
      throw new Error('Failed to create logo container session');
    }
    const uri = response.body.uri;
    const containerClient = new ContainerClient(uri);
    const logoName = `${id}.jpg`;
    await containerClient.uploadBlockBlob(logoName, file, file.size);
    await query.invalidateQueries(['mspLogo', id]);
    return `${logosBaseURL}/${logoName}`;
  });
};

interface DeleteLogoArgs {
  id: string;
}

export const useDeleteLogo = () => {
  return useAPIMutation((client, query) => async ({ id }: DeleteLogoArgs) => {
    const logoName = `${id}.jpg`;
    const response = await client.images.downloadStorageItem({
      query: { path: `logos/${logoName}` }
    });

    if (response.status !== 200) {
      throw new Error('Failed to create blob session');
    }
    const uri = response.body.uri;
    const blob = new BlobClient(uri);

    await blob.deleteIfExists();
    await query.invalidateQueries(['mspLogo', id]);
  });
};

export const useGetLogo = (id?: string) => {
  const logosBaseURL = useVariable<Variables>('logosBaseUrl');
  return useAPIQuery(['mspLogo', id], async client => {
    if (!id) return null;

    const logoName = `${id}.jpg`;
    const response = await client.images.downloadStorageItem({
      query: { path: `logos/${logoName}` }
    });

    if (response.status !== 200) {
      throw new Error('Failed to create blob session');
    }
    const uri = response.body.uri;
    const blob = new BlobClient(uri);

    // unfortunately cannot hide console errors if .exists does not find a blob
    return (await blob.exists()) ? `${logosBaseURL}/${blob.name}` : null;
  });
};

const ImageMetadata = z.object({
  id: z.string(),
  type: z.string(),
  name: z.string(),
  ratio: z.coerce.number(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
  original: z.string(),
  thumbnail: z.string(),
  hash: z.string()
});

export type ImageMetadata = z.infer<typeof ImageMetadata>;

export const useGallery = () => {
  const imageBaseURL = useVariable<Variables>('imageBaseUrl');
  return useAPIQuery(['gallery'], async client => {
    const response = await client.images.createImageUploadSession();
    if (response.status !== 200) {
      throw new Error('Failed to create image upload session');
    }
    const uri = response.body.uri;
    const containerClient = new ContainerClient(uri);
    const blobs = containerClient.listBlobsFlat({ includeMetadata: true });
    const images = [];
    for await (const blob of blobs) {
      if (blob.metadata?.type !== 'thumbnail' || !blob.metadata?.id) continue;
      const image = ImageMetadata.parse({
        ...blob.metadata,
        thumbnail: `${imageBaseURL}/${blob.name}`
      });
      images.push(image);
    }
    return images.sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
  });
};
